'use client';

import React, { useMemo } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Message } from '@blink/components/src/constants/messages';

import { MarginAccountSupplyAmountField } from './SupplyAmountField';

import { Store } from '@/shared/store';
import { showModalAction } from '@/shared/store/modals/actions';
import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { formatWithCommas, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { useModal, useVaultMarginAccountSupply } from '@/features/Modals/hooks';
import SupplyModalSchema, { SupplyModalData } from '@/shared/validations/supplyModal.schema';
import { ModalName } from '@/shared/store/modals/constants';
import { SummaryRow } from '@/widgets/Modals';
import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useMaxBalance } from '@/features/Modals/ui/MarginAccountVaultModals/Supply/hooks/useMaxBalance';

const SupplyModalTexts = {
    title: 'How much do you want to supply?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        poolUtilization: 'Total token supply being used by borrowers for their operations',
        poolBalance: 'Pool Balance shows the available tokens that have not been used by borrowers',
        netApy: 'Net APY is the total annual yield from your deposit, including interest and rewards.',
    },
};

enum MAVaultsSupplyModalFields {
    AMOUNT = 'amount',
}

export const MarginAccountsVaultsSupplyModal = () => {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const dispatch = useDispatch();
    const { modalProps } = useModal();
    const {
        tokenSymbol,
        totalDepositThreshold,
        decimals,
        poolUtilization,
        totalDeposited,
        refresh,
        apy,
    } = modalProps || {};

    const maxBalance = useMaxBalance();

    const methods = useForm<SupplyModalData>({
        resolver: yupResolver(SupplyModalSchema),
        reValidateMode: 'onChange',
        context: { maxBalance },
    });

    const { makeSupply } = useVaultMarginAccountSupply();

    const currentModal = {
        activeModal: ModalName.MARGIN_ACCOUNTS_VAULT_SUPPLY_MODAL,
        modalTitle: 'Supply Liquidity',
        modalProps,
    };

    const onSubmit = async (data: SupplyModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                    previousModal: currentModal,
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                    description: Message.PENDING_TEXT,
                },
                previousModal: currentModal,
            }),
        );

        await makeSupply({
            amount: data[MAVaultsSupplyModalFields.AMOUNT],
            agreementAddress: modalProps?.agreementAddress,
            marginAccountAddress: modalProps?.marginAccountAddress,
            tokenAddress: modalProps?.tokenAddress,
        });

        if (refresh) {
            await refresh();
        }
    };

    const supplyApy = Number(apy || 0).toFixed(2);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className='w-[448px] pt-7'>
                <Text className='mb-3 mt-2'>{SupplyModalTexts.title}</Text>
                <MarginAccountSupplyAmountField name={MAVaultsSupplyModalFields.AMOUNT} />

                <div className='mt-5'>
                    <TipCard text={SupplyModalTexts.tip} title='Tip' />
                </div>

                <div className='mt-4 border-b pb-4 border-border-opaque'>
                    <SummaryRow label='Current APY' value={`${supplyApy} %`} />
                    <SummaryRow label='Pool Utilization' value={`${poolUtilization} %`}>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.poolUtilization} />
                    </SummaryRow>
                    <SummaryRow label='Pool Balance' className='border-none'>
                        <div className='flex gap-2'>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                            >{`${formatWithCommas(
                                formatWithMaxPrecision(totalDeposited, decimals),
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                type={Text.types.Paragraph}
                                className='text-content-secondary'
                            >{`/ ${formatWithCommas(totalDepositThreshold)} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={SupplyModalTexts.tooltips.poolBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol } = modalProps || {};
    const amount = watch(MAVaultsSupplyModalFields.AMOUNT);
    const { error } = getFieldState(MAVaultsSupplyModalFields.AMOUNT);

    const getSubmitButtonText = () => {
        return !walletAddress ? 'Connect wallet' : `Approve ${tokenSymbol}`;
    };

    const maxBalance = useMaxBalance();

    const isDisabled = useMemo(() => {
        if (error || !amount || Number(amount) === 0) return true;
        if (!!walletAddress && !amount) return true;
        return Number(maxBalance) < Number(amount);
    }, [amount, maxBalance, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold mt-7'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
